import React from 'react';
import { Box, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import { GetFieldValue } from '../config/TabConfig';
import DescriptionIcon from '@mui/icons-material/Description';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export default function DocumentList({
  data,
  tabConfig,
  onPrintClick,
  onItemClick,
  searchRef,
  custList = [],
  tabName = null,
  subMenuId = null,
  mainMenuId = null,
  isDetail = false,
}) {
  // screentType이 'screen'인 경우 한 줄만 출력
  const displayData =
    tabConfig?.screentType === 'screen' && data && data.length > 0
      ? [data[0]]
      : data;

  if (!displayData || displayData.length === 0) {
    return (
      <Box
        sx={{
          height: 'calc(100% - 120px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#888',
          fontSize: '14px',
          fontWeight: 'bold',
        }}
      >
        조회된 데이터가 없습니다.
      </Box>
    );
  }

  return (
    <div
      style={{
        padding: '16px',
        paddingBottom: '16px',
        boxSizing: 'border-box',
      }}
    >
      {displayData.map((item, index) => (
        <li
          key={index}
          style={{
            width: '100%',
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            border: '1px solid #DFD9D9',
            padding: '0.8rem 1rem',
            borderRadius: '0.5rem',
            backgroundColor: 'transparent',
            fontSize: '16px',
            minHeight: '5rem',
            marginBottom: index === displayData.length - 1 ? '0' : '0.5rem',
            position: 'relative',
          }}
        >
          {/* 상세보기 버튼 (hasDetail일 때만 표시) */}
          {tabConfig?.hasDetail && onItemClick && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onItemClick(item);
              }}
              sx={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                padding: '4px',
                minWidth: 'auto',
                width: '28px',
                height: '28px',
                color: '#6c63e9ff',
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          )}

          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              width: '100%',
              paddingRight: tabConfig?.hasDetail && onItemClick ? '36px' : '0',
            }}
          >
            <div style={{ display: 'grid', height: '100%', width: '100%' }}>
              {/* 제목 */}
              <p
                style={{
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  margin: 0,
                  marginBottom: '0.5rem',
                }}
              >
                {(() => {
                  let cardTitle = '';

                  // screentType이 'screen'인 경우 파라미터에서 cardTitle 생성
                  if (
                    tabConfig?.screentType === 'screen' &&
                    searchRef?.current &&
                    tabConfig?.parameterMapping
                  ) {
                    const titleParts = [];

                    // _YEAR 파라미터 확인
                    const yearMapping = tabConfig.parameterMapping._YEAR;
                    if (yearMapping) {
                      const year =
                        searchRef.current[yearMapping] ||
                        searchRef.current.year ||
                        searchRef.current._year ||
                        searchRef.current.currentYear ||
                        searchRef.current._LW_YEAR ||
                        '';
                      if (year) titleParts.push(year);
                    }

                    // _YEAR_MONTH 파라미터 확인 (조회연월)
                    const yearMonthMapping =
                      tabConfig.parameterMapping._YEAR_MONTH;
                    if (yearMonthMapping) {
                      // _YEAR_MONTH는 currentYear와 month를 조합하여 표시 (FilterBottomSheet.js 참고)
                      const currentYear =
                        searchRef.current.currentYear ||
                        searchRef.current.year ||
                        searchRef.current._year ||
                        '';
                      const month =
                        searchRef.current.month ||
                        searchRef.current._month ||
                        '';

                      // YYYYMM 형식으로 조합
                      let yearMonth = '';
                      if (currentYear && month) {
                        // month가 한 자리 수면 앞에 0 추가
                        const monthStr =
                          month.length === 1 ? `0${month}` : month;
                        yearMonth = `${currentYear}${monthStr}`;
                      } else {
                        // 직접 저장된 값이 있으면 사용
                        yearMonth =
                          searchRef.current[yearMonthMapping] ||
                          searchRef.current.yearMonth ||
                          searchRef.current._YEAR_MONTH ||
                          '';
                      }

                      if (yearMonth) titleParts.push(yearMonth);
                    }

                    // _MONTH 파라미터 확인 (년도/월 선택 시 사용)
                    const monthMapping = tabConfig.parameterMapping._MONTH;
                    if (monthMapping) {
                      const month =
                        searchRef.current[monthMapping] ||
                        searchRef.current.month ||
                        searchRef.current._month ||
                        '';
                      if (month) titleParts.push(month);
                    }

                    // _QUATER 파라미터 확인
                    const quaterMapping = tabConfig.parameterMapping._QUATER;
                    if (quaterMapping) {
                      const quater =
                        searchRef.current[quaterMapping] ||
                        searchRef.current.quater ||
                        searchRef.current._quater ||
                        '';
                      if (quater) titleParts.push(quater);
                    }

                    // _CUST_CD 파라미터 확인
                    const custCdMapping = tabConfig.parameterMapping._CUST_CD;
                    if (custCdMapping) {
                      const custCd =
                        searchRef.current[custCdMapping] ||
                        searchRef.current.custCd ||
                        searchRef.current._custCd ||
                        searchRef.current._CUST_CD ||
                        '';

                      // 사업장명 찾기
                      if (custCd && custList && custList.length > 0) {
                        const cust = custList.find(
                          (c) => c.value === custCd || c.CUST_CD === custCd
                        );
                        const custName = cust?.label || cust?.CUST_NM || '';
                        if (custName) titleParts.push(custName);
                      }
                    }

                    // 탭 이름 또는 메뉴 이름
                    let menuName = tabName || '';
                    // 탭 이름이 없고 메뉴 ID가 있으면 메뉴 이름으로 변환
                    if (!menuName) {
                      const currentMenuId = subMenuId || mainMenuId;
                      if (currentMenuId) {
                        // 메뉴 ID를 메뉴 이름으로 변환 (간단한 매핑)
                        const menuNameMap = {
                          LegalCompliance: '법규준수평가',
                          SeriousAccidentAct: '중대재해처벌법 이행관리',
                          // 필요시 추가 메뉴 매핑
                        };
                        menuName = menuNameMap[currentMenuId] || currentMenuId;
                      }
                    }
                    if (menuName) titleParts.push(menuName);

                    // 조합
                    if (titleParts.length > 0) {
                      cardTitle = titleParts.join(' | ');
                    }
                  }

                  // screentType이 'screen'이 아니거나 cardTitle이 없는 경우 기존 로직 사용
                  if (!cardTitle && tabConfig) {
                    // 상세 화면이고 detailCardTitle이 있으면 우선 사용
                    if (
                      isDetail &&
                      tabConfig.detailCardTitle &&
                      Array.isArray(tabConfig.detailCardTitle)
                    ) {
                      const titleParts = tabConfig.detailCardTitle
                        .map((fieldName) => {
                          const value = item[fieldName];
                          return value !== undefined &&
                            value !== null &&
                            value !== ''
                            ? String(value)
                            : null;
                        })
                        .filter((part) => part !== null); // null이 아닌 값만 필터링

                      if (titleParts.length > 0) {
                        cardTitle = titleParts.join(' | ');
                      }
                    }
                    // detailCardTitle이 없거나 일반 화면인 경우 fieldMapping.cardTitle 확인
                    if (
                      !cardTitle &&
                      tabConfig.fieldMapping &&
                      tabConfig.fieldMapping.cardTitle &&
                      Array.isArray(tabConfig.fieldMapping.cardTitle)
                    ) {
                      const titleParts = tabConfig.fieldMapping.cardTitle
                        .map((fieldName) => {
                          const value = item[fieldName];
                          return value !== undefined &&
                            value !== null &&
                            value !== ''
                            ? String(value)
                            : null;
                        })
                        .filter((part) => part !== null); // null이 아닌 값만 필터링

                      if (titleParts.length > 0) {
                        cardTitle = titleParts.join(' | ');
                      }
                    }
                    // cardTitle이 없거나 값이 없으면 fileName 사용
                    if (!cardTitle && tabConfig.fieldMapping) {
                      cardTitle = GetFieldValue(
                        item,
                        tabConfig.fieldMapping.fileName
                      );
                    }
                  } else if (!cardTitle) {
                    cardTitle =
                      item.FILE_NAME ||
                      item.fileName ||
                      item.TITLE ||
                      '제목 없음';
                  }
                  return cardTitle || '제목 없음';
                })()}
              </p>

              {/* 인쇄물 버튼들 */}
              {(() => {
                // hidePrintItems가 true이고 MST 리스트(isDetail이 false)일 때만 인쇄물 버튼 숨김
                if (tabConfig?.hidePrintItems === true && !isDetail) {
                  return null;
                }

                // printItem이 있으면 printItem 사용
                if (
                  tabConfig?.printItem &&
                  Array.isArray(tabConfig.printItem) &&
                  tabConfig.printItem.length > 0
                ) {
                  return tabConfig.printItem.map((printItem, printIndex) => {
                    // printItem에서 필드명 또는 라벨 가져오기
                    let printItemName = '';
                    if (printItem.fieldName) {
                      printItemName = GetFieldValue(item, [
                        printItem.fieldName,
                      ]);
                      // 필드명이 없으면 버튼 표시 안 함
                      if (!printItemName) return null;
                    } else if (printItem.label) {
                      printItemName = printItem.label;
                    } else {
                      printItemName = `인쇄물 ${printIndex + 1}`;
                    }

                    return (
                      <Box
                        key={printIndex}
                        component="div"
                        sx={{
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: 'pointer',
                          padding: '4px 6px',
                          borderRadius: '6px',
                          transition: 'all 120ms ease-in-out',
                          minWidth: 0,
                          '&:hover': {
                            backgroundColor: '#F9F9FB',
                            boxShadow: 'inset 0 0 0 1px #ECECEC',
                          },
                        }}
                        role="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onPrintClick) {
                            onPrintClick(item, printItem);
                          }
                        }}
                      >
                        <DescriptionIcon
                          sx={{
                            fontSize: 18,
                            color: printItem.color || '#42A5F5',
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minWidth: 0,
                          }}
                        >
                          {printItemName}
                        </span>
                      </Box>
                    );
                  });
                }
                // printItem이 없으면 기본 버튼 표시
                else if (onPrintClick) {
                  return (
                    <Box
                      component="div"
                      sx={{
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        padding: '4px 6px',
                        borderRadius: '6px',
                        transition: 'all 120ms ease-in-out',
                        minWidth: 0,
                        '&:hover': {
                          backgroundColor: '#F9F9FB',
                          boxShadow: 'inset 0 0 0 1px #ECECEC',
                        },
                      }}
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPrintClick(item, null);
                      }}
                    >
                      <DescriptionIcon
                        sx={{
                          fontSize: 18,
                          color: '#42A5F5',
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minWidth: 0,
                        }}
                      >
                        미리보기
                      </span>
                    </Box>
                  );
                }
                return null;
              })()}

              {/* 작성자 정보 */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  {(() => {
                    const userValue = tabConfig
                      ? GetFieldValue(item, tabConfig.fieldMapping.regUserNm)
                      : item.REG_USER_NM || item.CREATE_USER_NM;
                    return (
                      userValue && (
                        <div
                          style={{
                            display: 'flex',
                            gap: '5px',
                            fontSize: '14px',
                          }}
                        >
                          <p
                            style={{
                              fontWeight: 'bold',
                              color: '#615e5eff',
                              margin: 0,
                            }}
                          >
                            {userValue}
                          </p>
                        </div>
                      )
                    );
                  })()}
                  {/* 날짜 */}
                  {(() => {
                    const dateValue = tabConfig
                      ? GetFieldValue(item, tabConfig.fieldMapping.date)
                      : item.REG_DT || item.DATE || item.CREATE_DATE;
                    return (
                      dateValue && (
                        <p
                          style={{
                            color: '#968787',
                            lineHeight: '1',
                            fontSize: '14px',
                            margin: 0,
                          }}
                        >
                          {dayjs(dateValue).format('YYYY-MM-DD')}
                        </p>
                      )
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </li>
      ))}
    </div>
  );
}
